import { useEffect, useState, type FormEvent } from 'react';
import { Plus, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useClients } from '@/hooks/useClients';
import { useCreateInvoice, useUpdateInvoice, type SaveInvoiceInput } from '@/hooks/useFinancials';
import {
  computeInvoiceTotals, daysFromTerms, PAYMENT_TERMS_LABELS,
  type Invoice, type InvoiceLineItem, type PaymentTerms,
} from '@/types/financials';
import { formatCurrency } from '@/lib/utils';

type Props = {
  open: boolean;
  onClose: () => void;
  invoice?: Invoice | null;
  defaultClientId?: string;
};

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (base: string, days: number) => {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const EMPTY: SaveInvoiceInput = {
  status: 'draft',
  clientId: '',
  issueDate: today(),
  dueDate: addDays(today(), 30),
  terms: 'net_30',
  lineItems: [],
  discountPct: 0,
  taxRatePct: 0.0975,
  shipping: 0,
  notes: '',
  termsAndConditions: 'Make checks payable to Talbert Architectural Panels & Doors, Inc. Late payments subject to 1.5% monthly finance charge.',
};

export function InvoiceFormModal({ open, onClose, invoice, defaultClientId }: Props) {
  const { data: clients } = useClients({ limit: 500 });
  const create = useCreateInvoice();
  const update = useUpdateInvoice();
  const editing = !!invoice;

  const [form, setForm] = useState<SaveInvoiceInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (invoice) {
        setForm(hydrate(invoice));
      } else {
        setForm({
          ...EMPTY,
          clientId: defaultClientId ?? '',
          issueDate: today(),
          dueDate: addDays(today(), 30),
        });
      }
      setError(null);
    }
  }, [open, invoice, defaultClientId]);

  function update_<K extends keyof SaveInvoiceInput>(key: K, val: SaveInvoiceInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function onTermsChange(t: PaymentTerms) {
    setForm((f) => ({
      ...f,
      terms: t,
      dueDate: t === 'custom' ? f.dueDate : addDays(f.issueDate, daysFromTerms(t)),
    }));
  }

  function onIssueDateChange(d: string) {
    setForm((f) => ({
      ...f,
      issueDate: d,
      dueDate: f.terms === 'custom' ? f.dueDate : addDays(d, daysFromTerms(f.terms)),
    }));
  }

  function setLineItems(items: InvoiceLineItem[]) {
    update_('lineItems', items);
  }

  function updateLine(idx: number, patch: Partial<InvoiceLineItem>) {
    setLineItems(form.lineItems.map((li, i) => (i === idx ? { ...li, ...patch } : li)));
  }

  function removeLine(idx: number) {
    setLineItems(form.lineItems.filter((_, i) => i !== idx));
  }

  function addLine() {
    setLineItems([
      ...form.lineItems,
      {
        id: crypto.randomUUID(),
        description: '',
        quantity: 1,
        unit: 'ea',
        unitPrice: 0,
        taxable: true,
      },
    ]);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.clientId) { setError('Please select a client.'); return; }
    if (form.lineItems.length === 0) { setError('Add at least one line item.'); return; }

    try {
      const totals = computeInvoiceTotals(form.lineItems, {
        discountPct: form.discountPct,
        taxRatePct: form.taxRatePct,
        shipping: form.shipping,
      });
      const payload = { ...form, totals };
      if (editing && invoice) {
        await update.mutateAsync({ id: invoice.id, ...payload });
        toast.success(`Updated ${invoice.number}`);
      } else {
        const result = await create.mutateAsync(payload);
        toast.success(`Created ${result.number}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;
  const totals = computeInvoiceTotals(form.lineItems, {
    discountPct: form.discountPct,
    taxRatePct: form.taxRatePct,
    shipping: form.shipping,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Invoice' : 'New Invoice'}
      title={editing ? `Edit ${invoice?.number}` : 'Create an invoice.'}
      description={editing ? undefined : 'Itemized invoice. Once sent, it hits AR and triggers the payment terms clock.'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="inv-form" loading={submitting} leftIcon={<FileText size={13} />}>
            {editing ? 'Save Changes' : 'Create Invoice'}
          </Button>
        </>
      }
    >
      <form id="inv-form" onSubmit={onSubmit} className="space-y-6 py-2">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Client"
            required
            value={form.clientId}
            onChange={(e) => update_('clientId', e.target.value)}
            options={[
              { value: '', label: '— Select a client —' },
              ...(clients ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <Input
            label="Linked Work Order"
            placeholder="Optional — WO-2601"
            value={form.orderNumber ?? ''}
            onChange={(e) => update_('orderNumber', e.target.value)}
            hint="Only for reference — not enforced."
          />
          <Input
            label="Issue Date"
            type="date"
            required
            value={form.issueDate.slice(0, 10)}
            onChange={(e) => onIssueDateChange(e.target.value)}
          />
          <Select
            label="Payment Terms"
            value={form.terms}
            onChange={(e) => onTermsChange(e.target.value as PaymentTerms)}
            options={Object.entries(PAYMENT_TERMS_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          />
          <Input
            label="Due Date"
            type="date"
            required
            value={form.dueDate.slice(0, 10)}
            onChange={(e) => update_('dueDate', e.target.value)}
            hint={form.terms !== 'custom' ? 'Auto-calculated from terms.' : 'Enter a custom due date.'}
            disabled={form.terms !== 'custom'}
          />
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="kicker">Line Items</div>
            <Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} onClick={addLine} type="button">
              Add Line
            </Button>
          </div>

          {form.lineItems.length === 0 ? (
            <div className="border border-dashed border-border rounded-sm p-6 text-center">
              <p className="text-sm text-text-muted mb-3">No line items yet.</p>
              <Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} onClick={addLine} type="button">
                Add First Line
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {form.lineItems.map((li, idx) => (
                <div key={li.id} className="bg-bg-subtle border border-border rounded-sm p-3">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-12 md:col-span-6">
                      <Input
                        label={idx === 0 ? 'Description' : undefined}
                        placeholder="Description"
                        value={li.description}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Input
                        label={idx === 0 ? 'Qty' : undefined}
                        type="number" min="0" step="any"
                        value={li.quantity}
                        onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Input
                        label={idx === 0 ? 'Unit' : undefined}
                        placeholder="ea"
                        value={li.unit ?? ''}
                        onChange={(e) => updateLine(idx, { unit: e.target.value })}
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Input
                        label={idx === 0 ? 'Price' : undefined}
                        type="number" min="0" step="0.01"
                        value={li.unitPrice}
                        onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="col-span-1 md:col-span-1 flex items-center justify-end">
                      <span className="text-sm text-text tabular whitespace-nowrap">
                        {formatCurrency(li.quantity * li.unitPrice)}
                      </span>
                    </div>
                    <div className="col-span-1 md:col-span-1 flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        className="p-1.5 text-text-dim hover:text-danger hover:bg-danger/10 rounded-sm"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Totals mini-panel */}
        <div className="bg-bg-subtle border border-border rounded-sm p-5">
          <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-border">
            <Input
              label="Discount %"
              type="number" min="0" max="100" step="0.1"
              value={form.discountPct !== undefined ? form.discountPct * 100 : ''}
              onChange={(e) => update_('discountPct', e.target.value === '' ? 0 : Number(e.target.value) / 100)}
            />
            <Input
              label="Tax Rate %"
              type="number" min="0" max="100" step="0.001"
              value={form.taxRatePct !== undefined ? form.taxRatePct * 100 : ''}
              onChange={(e) => update_('taxRatePct', e.target.value === '' ? 0 : Number(e.target.value) / 100)}
            />
            <Input
              label="Shipping"
              type="number" min="0" step="0.01"
              value={form.shipping ?? ''}
              onChange={(e) => update_('shipping', e.target.value === '' ? 0 : Number(e.target.value))}
            />
          </div>
          <dl className="space-y-1.5 text-sm">
            <Row label="Subtotal" value={formatCurrency(totals.subtotal)} />
            {totals.discount > 0 && <Row label="Discount" value={`− ${formatCurrency(totals.discount)}`} muted />}
            {totals.tax > 0 && <Row label={`Tax (${(totals.taxRate * 100).toFixed(2)}%)`} value={formatCurrency(totals.tax)} muted />}
            {totals.shipping > 0 && <Row label="Shipping" value={formatCurrency(totals.shipping)} muted />}
            <div className="pt-2 mt-2 border-t border-border-strong flex items-baseline justify-between">
              <dt className="kicker">Total</dt>
              <dd className="font-display text-2xl text-brass-300 tabular">{formatCurrency(totals.total)}</dd>
            </div>
          </dl>
        </div>

        {/* Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Textarea
            label="Customer-facing Notes"
            rows={3}
            placeholder="Thank you message, reference info…"
            value={form.notes ?? ''}
            onChange={(e) => update_('notes', e.target.value)}
          />
          <Textarea
            label="Internal Notes"
            rows={3}
            value={form.internalNotes ?? ''}
            onChange={(e) => update_('internalNotes', e.target.value)}
          />
        </div>

        <Textarea
          label="Terms & Conditions"
          rows={2}
          value={form.termsAndConditions ?? ''}
          onChange={(e) => update_('termsAndConditions', e.target.value)}
        />

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}

function hydrate(i: Invoice): SaveInvoiceInput {
  return {
    status: i.status, clientId: i.clientId, clientName: i.clientName, contactId: i.contactId,
    orderId: i.orderId, orderNumber: i.orderNumber,
    issueDate: i.issueDate?.slice(0, 10), dueDate: i.dueDate?.slice(0, 10),
    terms: i.terms, customTermsDays: i.customTermsDays,
    billTo: i.billTo,
    lineItems: i.lineItems ?? [],
    discountPct: i.discountPct ?? 0,
    taxRatePct: i.taxRatePct ?? 0,
    shipping: i.shipping ?? 0,
    notes: i.notes ?? '',
    internalNotes: i.internalNotes ?? '',
    termsAndConditions: i.termsAndConditions ?? '',
  };
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className={muted ? 'text-text-dim' : 'text-text-muted'}>{label}</dt>
      <dd className={`tabular ${muted ? 'text-text-muted' : 'text-text'}`}>{value}</dd>
    </div>
  );
}
